import { useEffect } from 'react'
import { loadMetaPixel } from '../utils/metaPixel'

/**
 * Loads the Meta Pixel ONLY on the first real user interaction.
 *
 * The Facebook script is ~240 KiB of third-party JavaScript. Loading it on a
 * timer meant it competed with the hero image for bandwidth and added long
 * tasks to the main thread on slow connections. Gating it behind an
 * interaction (scroll, tap, key, or pointer — anything a real visitor does
 * within the first second) keeps the initial load clean while still tracking
 * every human who actually engages.
 *
 * `loadMetaPixel` installs the `fbq` queue stub synchronously, so any event
 * fired on the same gesture that triggers the load is queued, not lost.
 */
export function useMetaPixel(): void {
  useEffect(() => {
    let hasLoaded = false

    const events: Array<keyof WindowEventMap> = [
      'pointerdown',
      'touchstart',
      'keydown',
      'scroll',
      'mousemove',
      'wheel',
    ]

    const start = () => {
      if (hasLoaded) return
      hasLoaded = true
      cleanup()
      loadMetaPixel()
    }

    const cleanup = () => {
      events.forEach((event) => window.removeEventListener(event, start))
    }

    events.forEach((event) =>
      window.addEventListener(event, start, { once: true, passive: true }),
    )

    return cleanup
  }, [])
}
